// src/hooks/useReferents.ts - Hooks pour la gestion des référents

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Referent } from '../types/referents';
import { useAuth } from '../contexts/AuthContext';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';

// ===== HOOKS POUR LES RÉFÉRENTS =====

/**
 * Hook pour récupérer tous les référents d'un business
 */
export const useReferents = (filters?: { isActive?: boolean } | null) => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  
  // Si filters est null, cela signifie qu'on ne doit pas charger (permission manquante)
  const shouldLoad = filters !== null;

  return useQuery({
    queryKey: ['referents', businessId, filters],
    queryFn: async () => {
      if (!businessId || !shouldLoad) {
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.referents),
          where('businessId', '==', businessId),
          orderBy('lastName', 'asc')
        );

        // Filtrer par statut actif si demandé
        if (filters?.isActive !== undefined) {
          q = query(q, where('isActive', '==', filters.isActive));
        }

        const snapshot = await getDocs(q).catch((error: any) => {
          handleFirestoreError(error, 'useReferents - Main query');
          throw error;
        });

        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            fullName: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          specialty: data.specialty || '',
          isActive: data.isActive !== false,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            createdBy: data.createdBy,
            notes: data.notes,
          } as Referent;
        });
      } catch (error) {
        console.error('Erreur dans useReferents:', error);
        return [];
      }
    },
    enabled: !!businessId && shouldLoad, // Ne pas charger si filters est null (pas de permission)
  });
};

/**
 * Hook pour récupérer un référent par ID
 */
export const useReferent = (referentId: string | undefined) => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['referent', businessId, referentId],
    queryFn: async () => {
      if (!referentId || !businessId) {
        return null;
      }

      try {
        const docRef = doc(db, COLLECTIONS.referents, referentId);
        const docSnap = await getDoc(docRef).catch((error: any) => {
          handleFirestoreError(error, 'useReferent - Get document');
          throw error;
        });

        if (!docSnap.exists()) {
          return null;
        }

        const data = docSnap.data();
        
        // Vérifier que le référent appartient au business
        if (data.businessId !== businessId) {
          return null;
        }

        return {
          id: docSnap.id,
          businessId: data.businessId,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          fullName: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          specialty: data.specialty || '',
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          notes: data.notes,
        } as Referent;
      } catch (error) {
        console.error('Erreur dans useReferent:', error);
        return null;
      }
    },
    enabled: !!referentId && !!businessId,
  });
};

/**
 * Hook pour créer un référent
 */
export const useAddReferent = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (referentData: {
      firstName: string;
      lastName: string;
      specialty: string;
      notes?: string;
      isActive?: boolean;
    }) => {
      if (!businessId || !userProfile) {
        throw new Error('Business ou utilisateur non défini');
      }

      // Validation
      if (!referentData.firstName.trim() || !referentData.lastName.trim()) {
        throw new Error('Le prénom et le nom sont obligatoires');
      }

      if (!referentData.specialty.trim()) {
        throw new Error('La spécialité est obligatoire');
      }

      const fullName = `${referentData.firstName.trim()} ${referentData.lastName.trim()}`;

      const newReferent = {
        businessId,
        firstName: referentData.firstName.trim(),
        lastName: referentData.lastName.trim(),
        fullName,
        specialty: referentData.specialty.trim(),
        isActive: referentData.isActive !== false,
        notes: referentData.notes?.trim() || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: userProfile.id,
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.referents), newReferent).catch((error: any) => {
        handleFirestoreError(error, 'useAddReferent - Add document');
        throw error;
      });

      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referents', businessId] });
    },
  });
};

/**
 * Hook pour modifier un référent
 */
export const useUpdateReferent = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (referentData: {
      id: string;
      firstName?: string;
      lastName?: string;
      specialty?: string;
      notes?: string;
      isActive?: boolean;
    }) => {
      if (!businessId || !userProfile) {
        throw new Error('Business ou utilisateur non défini');
      }

      const referentRef = doc(db, COLLECTIONS.referents, referentData.id);
      
      // Vérifier que le référent existe et appartient au business
      const referentSnap = await getDoc(referentRef);
      if (!referentSnap.exists()) {
        throw new Error('Référent introuvable');
      }

      const existingData = referentSnap.data();
      if (existingData.businessId !== businessId) {
        throw new Error('Ce référent n\'appartient pas à votre business');
      }

      // Construire les données à mettre à jour
      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      if (referentData.firstName !== undefined) {
        updateData.firstName = referentData.firstName.trim();
      }

      if (referentData.lastName !== undefined) {
        updateData.lastName = referentData.lastName.trim();
      }

      if (referentData.firstName !== undefined || referentData.lastName !== undefined) {
        const firstName = updateData.firstName || existingData.firstName;
        const lastName = updateData.lastName || existingData.lastName;
        updateData.fullName = `${firstName} ${lastName}`.trim();
      }

      if (referentData.specialty !== undefined) {
        updateData.specialty = referentData.specialty.trim();
      }

      if (referentData.isActive !== undefined) {
        updateData.isActive = referentData.isActive;
      }

      if (referentData.notes !== undefined) {
        updateData.notes = referentData.notes?.trim() || null;
      }

      await updateDoc(referentRef, updateData).catch((error: any) => {
        handleFirestoreError(error, 'useUpdateReferent - Update document');
        throw error;
      });

      return referentData.id;
    },
    onSuccess: (referentId) => {
      queryClient.invalidateQueries({ queryKey: ['referents', businessId] });
      queryClient.invalidateQueries({ queryKey: ['referent', businessId, referentId] });
    },
  });
};

/**
 * Hook pour supprimer un référent (soft delete)
 */
export const useDeleteReferent = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (referentId: string) => {
      if (!businessId) {
        throw new Error('Business non défini');
      }

      const referentRef = doc(db, COLLECTIONS.referents, referentId);
      
      // Vérifier que le référent existe et appartient au business
      const referentSnap = await getDoc(referentRef);
      if (!referentSnap.exists()) {
        throw new Error('Référent introuvable');
      }

      const existingData = referentSnap.data();
      if (existingData.businessId !== businessId) {
        throw new Error('Ce référent n\'appartient pas à votre business');
      }

      // Soft delete : désactiver au lieu de supprimer
      await updateDoc(referentRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      }).catch((error: any) => {
        handleFirestoreError(error, 'useDeleteReferent - Update document');
        throw error;
      });

      return referentId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referents', businessId] });
    },
  });
};

